/**
 * Період блоку «Виробнича звітність» — чиста арифметика без верстки.
 *
 * Дата тут ЛОКАЛЬНА: `toISOString()` переводить у UTC, і в Києві о 01:00
 * «сьогодні» перетворилось би на вчорашній день. Тому і збирання рядка
 * YYYY-MM-DD, і зсув на N днів робляться через локальні компоненти дати.
 */

/** Кнопки перемикача. `custom` не має кнопки — це один день із «останнього звіту». */
export type ActivityPreset = 'yesterday' | 'today' | '7d' | '30d';
export type ActivityRangeKind = ActivityPreset | 'custom';

export interface ActivityRange {
  preset: ActivityRangeKind;
  /** YYYY-MM-DD, включно */
  from: string;
  /** YYYY-MM-DD, включно */
  to: string;
}

const LONG_DAY: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
const SHORT_DAY: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };

/** `Date` → `YYYY-MM-DD` за локальним календарем. */
export function isoDate(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** `YYYY-MM-DD` → локальна опівнічна дата (а не UTC, як робить `new Date(iso)`). */
export function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Той самий час доби, інший день. Перехід через місяць/рік робить сам Date. */
export function shiftDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Межі періоду для кнопки перемикача. Обидві дати включні. */
export function presetRange(preset: ActivityPreset, today: Date = new Date()): ActivityRange {
  const to = preset === 'yesterday' ? shiftDays(today, -1) : today;
  const from =
    preset === 'yesterday'
      ? to
      : preset === 'today'
        ? today
        : shiftDays(today, preset === '7d' ? -6 : -29);
  return { preset, from: isoDate(from), to: isoDate(to) };
}

/** «2 вер.» — короткий підпис дати для рядків і посилань. */
export function shortDay(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString('uk-UA', SHORT_DAY);
}

/** Людський підпис періоду: «сьогодні», «вчора, 2 вересня», «27 серп. – 2 вер.». */
export function periodLabel(range: ActivityRange, today: Date = new Date()): string {
  if (range.from !== range.to) {
    return `${shortDay(range.from)} – ${shortDay(range.to)}`;
  }
  const day = parseIsoDate(range.from);
  const long = day.toLocaleDateString('uk-UA', LONG_DAY);
  if (range.from === isoDate(today)) return 'сьогодні';
  if (range.from === isoDate(shiftDays(today, -1))) return `вчора, ${long}`;
  return long;
}

/** «1 звіт», «2 звіти», «5 звітів» — українська відмінкова форма. */
export function pluralReports(count: number): string {
  const n = Math.abs(count) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) return `${count} звітів`;
  if (last === 1) return `${count} звіт`;
  if (last >= 2 && last <= 4) return `${count} звіти`;
  return `${count} звітів`;
}
